import { useAuthenticatedClient } from './useApi';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';

export const useConsultantOrgApi = (orgId: string | undefined) => {
  const { client } = useAuthenticatedClient(false);
  return useQuery({
    queryKey: ['consultant-org', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('No orgId');
      const response = await client.consultant.getOrg({
        params: { orgId },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch org');
    },
    enabled: !!orgId,
  });
};

export const useConsultantWorkspacesApi = (orgId: string | undefined) => {
  const { client } = useAuthenticatedClient(false);
  return useQuery({
    queryKey: ['consultant-workspaces', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('No orgId');
      const response = await client.consultant.listWorkspaces({
        params: { orgId },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch consultant workspaces');
    },
    enabled: !!orgId,
  });
};

export const useConsultantUpdatesApi = (
  orgId: string | undefined,
  query: {
    fromDate?: string;
    toDate?: string;
    source?: string;
    geography?: string;
    kind?: string;
    documentType?: string;
  }
) => {
  const { client } = useAuthenticatedClient(false);
  return useQuery({
    queryKey: ['consultant-updates', orgId, query],
    queryFn: async () => {
      if (!orgId) throw new Error('No orgId');
      const response = await client.consultant.listUpdates({
        params: { orgId },
        query,
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch consultant updates');
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
};

export const useConsultantUpsertSummaryApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      updateId,
      body,
    }: {
      orgId: string;
      updateId: string;
      body: {
        shortSummary: string | null;
        summary: string | null;
        fileId?: string | null;
      };
    }) => {
      const response = await client.consultant.upsertSummary({
        params: { orgId, updateId },
        body,
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to save summary');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultant-updates'] });
    },
  });
};

export const useConsultantUploadFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      updateId,
      file,
    }: {
      orgId: string;
      updateId: string;
      file: File;
    }) => {
      const response = await client.consultant.uploadAnalysisFile({
        params: { orgId, updateId },
        body: { file },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to upload file');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultant-updates'] });
    },
  });
};

export const useConsultantTriageUpdatesApi = (
  orgId: string | undefined,
  query: {
    fromDate?: string;
    toDate?: string;
    source?: string;
    geography?: string;
    kind?: string;
    documentType?: string;
  }
) => {
  const { client } = useAuthenticatedClient(false);
  return useQuery({
    queryKey: ['consultant-triage', orgId, query],
    queryFn: async () => {
      if (!orgId) throw new Error('No orgId');
      const response = await client.consultant.listTriageUpdates({
        params: { orgId },
        query,
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch triage updates');
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
};

export const useConsultantTriageApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      updateId,
      workspaceId,
      status,
    }: {
      orgId: string;
      updateId: string;
      workspaceId: string;
      status: 'applicable' | 'not_applicable';
    }) => {
      const response = await client.consultant.triageUpdate({
        params: { orgId, updateId },
        body: { workspaceId, status },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to save triage');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultant-triage'] });
    },
  });
};

export const useConsultantDeleteFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      updateId,
    }: {
      orgId: string;
      updateId: string;
    }) => {
      const response = await client.consultant.deleteAnalysisFile({
        params: { orgId, updateId },
        body: undefined as never,
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to delete file');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultant-updates'] });
    },
  });
};
